/**
 * Variant State Filter
 * Filters product variants based on user's state.
 *
 * Uses a single dynamic CSS rule (updating the <style id="variant-state-style"> tag)
 * so all variants are correctly shown/hidden whenever the state changes,
 * including when the user switches zip codes mid-session.
 *
 * When NO variants are available for the user's state, the variant picker form
 * is hidden, an unavailability notice is shown, and the add-to-cart buttons
 * (including the sticky bar) are disabled.
 */

class VariantStateFilter {
  static NOTICE_CLASS = 'variant-state-unavailable-notice';

  constructor() {
    this.isEnabled = window.allowedStatesEnabled === true;
    if (!this.isEnabled) return;

    this.injectNoticeStyles();
    this.setupEventListeners();
  }

  getStyleTag() {
    let style = document.getElementById('variant-state-style');
    if (!style) {
      style = document.createElement('style');
      style.id = 'variant-state-style';
      document.head.appendChild(style);
    }
    return style;
  }

  injectNoticeStyles() {
    if (document.getElementById('variant-state-notice-styles')) return;
    const style = document.createElement('style');
    style.id = 'variant-state-notice-styles';
    style.textContent = `
      .${VariantStateFilter.NOTICE_CLASS} {
        border: 2px solid #C45A3A;
        background: #fff;
        color: #C45A3A;
        border-radius: 10px;
        padding: 14px 16px;
        font-size: 1em;
        font-weight: 600;
        line-height: 1.5;
        text-align: center;
        box-shadow: 0 6px 20px rgba(196, 90, 58, 0.22);
        margin-block: 0.5em;
      }
      .${VariantStateFilter.NOTICE_CLASS} p {
        margin: 0;
      }
      .${VariantStateFilter.NOTICE_CLASS} a {
        color: #C45A3A;
        font-weight: 700;
        text-decoration: underline;
      }
      .${VariantStateFilter.NOTICE_CLASS} a:hover {
        color: #8B3A25;
      }
    `;
    document.head.appendChild(style);
  }

  applyState(state) {
    const style = this.getStyleTag();

    if (!state) {
      style.textContent = '';
      this.clearAllUnavailableStates();
      return;
    }

    style.textContent = [
      `.variant-option__button-label:has(input[data-allowed-states]:not([data-allowed-states*="${state}"])) { display: none !important; }`,
      `option[data-allowed-states]:not([data-allowed-states*="${state}"]) { display: none !important; }`
    ].join('\n');

    this.reSelectAllPickers(state);
    this.checkAllPickersAvailability(state);
  }

  isAllowed(element, state) {
    const allowedStates = element?.dataset?.allowedStates;
    if (!allowedStates) return true;
    return allowedStates.split(',').map(s => s.trim()).includes(state);
  }

  reSelectAllPickers(state) {
    document.querySelectorAll('variant-picker').forEach(picker => {
      this.reSelectPicker(picker, state);
    });
  }

  reSelectPicker(picker, state) {
    picker.querySelectorAll('.variant-option--buttons').forEach(fieldset => {
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      if (!checked || this.isAllowed(checked, state)) return;

      const all = fieldset.querySelectorAll('input[type="radio"]');
      for (const input of all) {
        if (this.isAllowed(input, state)) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
          break;
        }
      }
    });

    picker.querySelectorAll('.variant-option__select').forEach(select => {
      const selected = select.options[select.selectedIndex];
      if (!selected || this.isAllowed(selected, state)) return;

      for (const option of select.options) {
        if (!option.disabled && this.isAllowed(option, state)) {
          option.selected = true;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          break;
        }
      }
    });
  }

  // ── Availability check ────────────────────────────────────────────────

  checkAllPickersAvailability(state) {
    document.querySelectorAll('variant-picker').forEach(picker => {
      this.checkPickerAvailability(picker, state);
    });
  }

  checkPickerAvailability(picker, state) {
    const hasRestrictions = picker.querySelector('[data-allowed-states]');
    if (!hasRestrictions) {
      this.setPickerAvailable(picker);
      return;
    }

    const allInputs = picker.querySelectorAll(
      'input[type="radio"], .variant-option__select option'
    );
    const hasAvailable = Array.from(allInputs).some(el => this.isAllowed(el, state));

    if (hasAvailable) {
      this.setPickerAvailable(picker);
    } else {
      this.setPickerUnavailable(picker);
    }
  }

  setPickerUnavailable(picker) {
    const form = picker.querySelector('.variant-picker__form');
    if (form) form.style.display = 'none';

    let notice = picker.querySelector('.' + VariantStateFilter.NOTICE_CLASS);
    if (!notice) {
      const message = window.allowedStatesNoticeMessage
        || 'This product is not available for shipping to your state. Please <a href="/pages/contact-us">contact us</a> for more information.';

      notice = document.createElement('div');
      notice.className = VariantStateFilter.NOTICE_CLASS;
      notice.setAttribute('role', 'alert');
      notice.innerHTML = message;
      picker.prepend(notice);
    }
    notice.style.display = '';

    this.disableBuyButtons(picker);
  }

  setPickerAvailable(picker) {
    const form = picker.querySelector('.variant-picker__form');
    if (form) form.style.display = '';

    const notice = picker.querySelector('.' + VariantStateFilter.NOTICE_CLASS);
    if (notice) notice.style.display = 'none';

    this.enableBuyButtons(picker);
  }

  clearAllUnavailableStates() {
    document.querySelectorAll('variant-picker').forEach(picker => {
      this.setPickerAvailable(picker);
    });
  }

  // ── Buy-button helpers ────────────────────────────────────────────────

  findBuyButtonContainers(picker) {
    const containers = [];
    const section = picker.closest('.shopify-section, .product-information');

    if (section) {
      section.querySelectorAll('product-form-component').forEach(pfc => {
        containers.push(pfc);
      });
    }

    // Sticky product bar (lives outside the section)
    const stickyBar = document.querySelector('[data-sticky-product-bar] product-form-component');
    if (stickyBar) containers.push(stickyBar);

    return containers;
  }

  disableBuyButtons(picker) {
    this.findBuyButtonContainers(picker).forEach(container => {
      const btn = container.querySelector('button[name="add"]');
      if (btn) {
        btn.disabled = true;
        btn.dataset.stateUnavailable = 'true';
      }

      const accel = container.querySelector('[ref="acceleratedCheckoutButtonContainer"]');
      if (accel) {
        accel.setAttribute('hidden', '');
        accel.dataset.stateUnavailable = 'true';
      }
    });
  }

  enableBuyButtons(picker) {
    this.findBuyButtonContainers(picker).forEach(container => {
      const btn = container.querySelector('button[name="add"][data-state-unavailable]');
      if (btn) {
        btn.disabled = false;
        delete btn.dataset.stateUnavailable;
      }

      const accel = container.querySelector('[data-state-unavailable][ref="acceleratedCheckoutButtonContainer"]');
      if (accel) {
        accel.removeAttribute('hidden');
        delete accel.dataset.stateUnavailable;
      }
    });
  }

  // ── Event listeners & observers ───────────────────────────────────────

  setupEventListeners() {
    const storedState = localStorage.getItem('user_state');
    if (storedState) {
      this.applyState(storedState);
    }

    window.addEventListener('zipcodeUpdated', (e) => {
      const newState = e.detail?.state || localStorage.getItem('user_state');
      this.applyState(newState);
    });

    this.observeVariantPickers();
  }

  observeVariantPickers() {
    let timeoutId = null;

    new MutationObserver((mutations) => {
      let hasNewPickers = false;

      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;
          if (node.tagName === 'VARIANT-PICKER' || node.querySelector?.('variant-picker')) {
            hasNewPickers = true;
          }
        });
      });

      if (hasNewPickers) {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          const state = localStorage.getItem('user_state');
          if (state) {
            this.reSelectAllPickers(state);
            this.checkAllPickersAvailability(state);
          }
        }, 100);
      }
    }).observe(document.body, { childList: true, subtree: true });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new VariantStateFilter());
} else {
  new VariantStateFilter();
}
